import React, { useState, useEffect } from 'react';
import { Button, Grid, makeStyles, IconButton, Tooltip } from '@material-ui/core';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import TimePicker, { TimePickerValue } from 'react-time-picker';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import TimerIcon from '@material-ui/icons/Timer';
import UpdateIcon from '@material-ui/icons/Update';
import { usePrevious } from '../../hooks';
import '../common/react-time-picker.css';
dayjs.extend(duration);
dayjs.extend(utc);

const useStyles = makeStyles((theme) => ({
	grid: {
		textAlign: 'center',
		marginBottom: theme.spacing(4)
	},
	button: {
		width: 'max-content',
		margin: 'auto',
		marginRight: theme.spacing(1)
	},
	time: {
		display: 'inline-block',
		minWidth: '190px'
	},
	flip: {
		transform: 'rotate(180deg)'
	},
	title: {
		marginBottom: '0'
	},
	error: {
		color: theme.palette.error.main,
		marginTop: 0
	}
}));

const ClipperTime: React.FC<ClipperTimeProps> = (props) => {
	const { title, cutPosition, currentTime, start, warning, setCutPosition, seek } = props;
	const classes = useStyles();
	const [value, setValue] = useState<Date>(new Date());
	const previousCutPosition = usePrevious(cutPosition);

	useEffect(() => {
		if (cutPosition && cutPosition !== previousCutPosition) {
			setValue(dayjs(cutPosition).toDate());
		}
	}, [cutPosition, previousCutPosition]);

	const seekAndSetNewTime = (timeDifference: number): void => {
		const currentPosition = cutPosition ?? dayjs(start).add(currentTime, 'second').toISOString();
		const newPosition = dayjs(currentPosition).add(timeDifference, 'ms');
		handleValueChange(newPosition);
	};

	const handleValueChange = (newPosition: dayjs.Dayjs): void => {
		const newPositionString = newPosition.toISOString();
		setCutPosition(newPosition.utc().local().format('YYYY-MM-DDTHH:mm:ssZ'));
		seek(newPositionString, true);
	};

	let isFuture = false;
	if (cutPosition && dayjs(Date.now()).isBefore(dayjs(cutPosition))) {
		isFuture = true;
	}

	return (
		<Grid container item xs={12} direction='column' spacing={1} className={classes.grid}>
			<h3 className={classes.title}>{title}</h3>
			<Grid>
				<IconButton data-testid='seek-5-sec-earlier-btn' aria-label='5 seconds earlier' onClick={(): void => seekAndSetNewTime(-5000)}>
					<DoubleArrowIcon className={classes.flip} />
				</IconButton>
				<IconButton data-testid='seek-1-sec-earlier-btn' aria-label='1 second earlier' onClick={(): void => seekAndSetNewTime(-1000)}>
					<KeyboardArrowLeftIcon />
				</IconButton>
				{cutPosition ? (
					<TimePicker
						data-testid='time-picker'
						name='startTime'
						value={value}
						format='HH:mm:ss'
						maxDetail='second'
						onChange={(value: TimePickerValue): void => {
							if (typeof value === 'string') {
								const timeParts = value.split(':');
								const newValue = dayjs(start)
									.set('hour', Number(timeParts[0]))
									.set('minute', Number(timeParts[1]))
									.set('second', Number(timeParts[2]));
								handleValueChange(newValue);
							}
						}}
						clearIcon={null}
						disableClock={true}
					/>
				) : (
					<p data-testid='no-time-msg' className={classes.time}>
						No Time
					</p>
				)}
				<IconButton data-testid='seek-1-sec-later-btn' aria-label='1 second later' onClick={(): void => seekAndSetNewTime(1000)}>
					<KeyboardArrowRightIcon />
				</IconButton>
				<IconButton data-testid='seek-5-sec-later-btn' aria-label='5 seconds later' onClick={(): void => seekAndSetNewTime(5000)}>
					<DoubleArrowIcon />
				</IconButton>
			</Grid>
			{isFuture && (
				<p data-testid='future-time-warning-msg' className={classes.error}>
					Cut times are not allowed to be in the future
				</p>
			)}
			{warning && (
				<p data-testid='warning-msg' className={classes.error}>
					{warning}
				</p>
			)}
			<Grid>
				<Tooltip title='Select the time of the livestream video'>
					<Button
						data-testid='set-time-btn'
						className={classes.button}
						onClick={(): void => setCutPosition(dayjs(start).utc().local().add(currentTime, 'second').format('YYYY-MM-DDTHH:mm:ssZ'))}
						variant='contained'
						startIcon={<TimerIcon />}>
						Set Current Time
					</Button>
				</Tooltip>
				<Tooltip title='Go to current time that you have set'>
					<Button
						data-testid='go-to-time-btn'
						className={classes.button}
						onClick={(): void | null => (cutPosition ? seek(cutPosition) : null)}
						variant='contained'
						startIcon={<UpdateIcon />}>
						Go to Set Time
					</Button>
				</Tooltip>
			</Grid>
		</Grid>
	);
};

type ClipperTimeProps = {
	title: string; // clipper title
	currentTime: number; // current video position
	start: string; // airing start time
	cutPosition?: string | null | undefined; // clip cut position
	setCutPosition: (time: string) => void; // set clip cut position
	seek: (seekingPostion: string, pause?: boolean) => void; // resets video playback
	warning?: string | null; // warning/validation messages
};

export default ClipperTime;
